import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { Icon } from '../Icon';
import { Alignment, Popup, type PopupContext, Position, type TriggerEvents, findFirstFocusableChildOrRoot, findFocusableChildren, usePopupContext } from '../Popup';
import { findFirstFocusableChild, findNextFocusableElementExcludingChildren, findNextFocusableSibling, findPreviousFocusableSibling, tick } from '../Popup';
import { Keys, type MaybeHtml } from '../Popup/Popup';
import selectStyles from '../Select/Select.module.css';
import wrapperStyles from '../TextInputWrapper/TextInputWrapper.module.css';
import styles from './Dropdown.module.css';
import { DropdownProps } from './types';
const triggerEvents: TriggerEvents = ['click', 'keypress'];
export function DropdownTrigger(t0) {
  const $ = _c(8);
  const {
    children,
    style,
    className,
    id
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(selectStyles.selectContainer, selectStyles.select, styles.dropdownTriggerButton, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon name="chevronDown" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== id || $[5] !== style || $[6] !== t1) {
    t3 = <button id={id} style={style} className={t1} type="button">{children}{t2}</button>;
    $[3] = children;
    $[4] = id;
    $[5] = style;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function DropdownLabel(t0) {
  const $ = _c(8);
  const {
    children,
    className,
    required,
    hint,
    id
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className || $[2] !== hint || $[3] !== id || $[4] !== required) {
    t1 = children ? <label htmlFor={id} className={classNames(wrapperStyles.label, className)}><div>{children}{required && <span aria-label="(required)">*</span>}</div>{hint ? <div className={classNames(wrapperStyles.hint)}>{hint}</div> : null}</label> : null;
    $[0] = children;
    $[1] = className;
    $[2] = hint;
    $[3] = id;
    $[4] = required;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <div>{t1}</div>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DropdownMenu(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.dropdownMenu, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <ul className={t1}>{children}</ul>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function DropdownMenuItem(t0) {
  const $ = _c(15);
  const {
    children,
    style,
    className
  } = t0;
  const ref = useRef(null);
  const {
    preferredPopupPosition,
    hidePopup,
    parentContext,
    spyRef
  } = useDropdownContext();
  let t1;
  if ($[0] !== hidePopup || $[1] !== parentContext || $[2] !== preferredPopupPosition || $[3] !== spyRef) {
    t1 = event => {
      const position = parentContext && preferredPopupPosition;
      const closePopupKey = position && PositionToArrowKey[position].close;
      if (event.key === Keys.Down || event.key === Keys.Up) {
        event.preventDefault();
        const siblingToFocus = event.key === Keys.Up ? findPreviousFocusableSibling(ref.current) : findNextFocusableSibling(ref.current);
        siblingToFocus?.focus();
        return;
      }
      if (event.key === closePopupKey) {
        event.preventDefault();
        findFirstFocusableChildOrRoot(spyRef?.current?.nextElementSibling)?.focus();
        hidePopup();
      }
      if (event.key === Keys.Enter && event.target instanceof HTMLElement && "click" in event.target) {
        event.target.click();
      }
    };
    $[0] = hidePopup;
    $[1] = parentContext;
    $[2] = preferredPopupPosition;
    $[3] = spyRef;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const onKeyDown = t1;
  let t2;
  let t3;
  if ($[5] !== hidePopup || $[6] !== onKeyDown) {
    t2 = () => {
      const ele = findFirstFocusableChild(ref.current);
      if (!ele) {
        return;
      }
      ele.addEventListener("keydown", onKeyDown);
      ele.addEventListener("click", hidePopup);
      return () => {
        ele.removeEventListener("keydown", onKeyDown);
        ele.removeEventListener("click", hidePopup);
      };
    };
    t3 = [hidePopup, onKeyDown];
    $[5] = hidePopup;
    $[6] = onKeyDown;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== className) {
    t4 = classNames(styles.dropdownMenuItem, className);
    $[9] = className;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== children || $[12] !== style || $[13] !== t4) {
    t5 = <li ref={ref} style={style} className={t4}>{children}</li>;
    $[11] = children;
    $[12] = style;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
export function DropdownMenuSeparator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <li className={styles.dropdownMenuSeparator} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const PositionToArrowKey = {
  [Position.BOTTOM]: {
    open: Keys.Down,
    close: Keys.Down
  },
  [Position.TOP]: {
    open: Keys.Up,
    close: Keys.Down
  },
  [Position.RIGHT]: {
    open: Keys.Right,
    close: Keys.Left
  },
  [Position.LEFT]: {
    open: Keys.Left,
    close: Keys.Right
  }
} as const;
function DropdownTriggerEventHandler(t0) {
  const $ = _c(9);
  const {
    children
  } = t0;
  const {
    spyRef,
    popupRef,
    showPopup,
    preferredPopupPosition
  } = useDropdownContext();
  let t1;
  if ($[0] !== popupRef || $[1] !== preferredPopupPosition || $[2] !== showPopup || $[3] !== spyRef) {
    t1 = event => {
      const position = preferredPopupPosition ?? Position.BOTTOM;
      const openPopupKey = PositionToArrowKey[position].open;
      if (event.key !== openPopupKey) {
        return;
      }
      event.preventDefault();
      showPopup();
      tick().then(() => {
        let focusable = findFirstFocusableChild(popupRef?.current);
        if (focusable) {
          focusable.focus();
          return;
        }
        if (event.key !== Keys.Tab) {
          return;
        }
        focusable = findNextFocusableElementExcludingChildren(spyRef?.current?.nextElementSibling);
        focusable?.focus();
      });
    };
    $[0] = popupRef;
    $[1] = preferredPopupPosition;
    $[2] = showPopup;
    $[3] = spyRef;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const onKeyDown = t1;
  let t2;
  let t3;
  if ($[5] !== onKeyDown || $[6] !== spyRef) {
    t2 = () => {
      const ele = spyRef?.current?.nextElementSibling as MaybeHtml;
      if (!ele) {
        return;
      }
      ele.addEventListener("keydown", onKeyDown);
      return () => ele.removeEventListener("keydown", onKeyDown);
    };
    t3 = [onKeyDown, spyRef];
    $[5] = onKeyDown;
    $[6] = spyRef;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  return children;
}
function DropdownContentEventHandler(t0) {
  const $ = _c(16);
  const {
    children
  } = t0;
  const {
    spyRef,
    popupRef,
    hidePopup,
    open
  } = useDropdownContext();
  let t1;
  if ($[0] !== hidePopup || $[1] !== spyRef) {
    t1 = event => {
      if (event.key !== Keys.Tab || !event.shiftKey) {
        return;
      }
      event.preventDefault();
      findFirstFocusableChildOrRoot(spyRef?.current?.nextElementSibling)?.focus();
      hidePopup();
    };
    $[0] = hidePopup;
    $[1] = spyRef;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleFirstElement = t1;
  let t2;
  if ($[3] !== spyRef) {
    t2 = event_0 => {
      if (event_0.key !== Keys.Tab || event_0.shiftKey) {
        return;
      }
      event_0.preventDefault();
      const focusable = findNextFocusableElementExcludingChildren(spyRef?.current?.nextElementSibling);
      focusable?.focus();
    };
    $[3] = spyRef;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleLastElement = t2;
  let t3;
  if ($[5] !== handleFirstElement || $[6] !== handleLastElement || $[7] !== popupRef) {
    t3 = () => {
      const ele = popupRef?.current as MaybeHtml;
      if (!ele) {
        return;
      }
      const elements = findFocusableChildren(ele);
      if (!elements || !elements.length) {
        return;
      }
      const first = elements.item(0);
      const last = elements.item(elements.length - 1);
      first.addEventListener("keydown", handleFirstElement);
      last.addEventListener("keydown", handleLastElement);
      return () => {
        first.removeEventListener("keydown", handleFirstElement);
        last.removeEventListener("keydown", handleLastElement);
      };
    };
    $[5] = handleFirstElement;
    $[6] = handleLastElement;
    $[7] = popupRef;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== handleFirstElement || $[11] !== handleLastElement || $[12] !== open || $[13] !== popupRef || $[14] !== spyRef) {
    t4 = [handleFirstElement, handleLastElement, spyRef, popupRef, children, open];
    $[9] = children;
    $[10] = handleFirstElement;
    $[11] = handleLastElement;
    $[12] = open;
    $[13] = popupRef;
    $[14] = spyRef;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  useEffect(t3, t4);
  return children;
}
function Dropdown(t0) {
  const $ = _c(19);
  const {
    trigger,
    children,
    className,
    alignContent: t1,
    wrapperClassName,
    anchorClassName,
    preferredPopupPosition: position,
    marginInPx,
    Anchor,
    id
  } = t0;
  const alignContent = t1 === undefined ? Alignment.JUSTIFY : t1;
  const {
    parentContext
  } = useDropdownContext();
  const preferredPopupPosition = position ?? (parentContext ? parentContext.preferredPopupPosition ?? Position.RIGHT : Position.BOTTOM);
  let t2;
  if ($[0] !== children) {
    t2 = <DropdownContentEventHandler>{children}</DropdownContentEventHandler>;
    $[0] = children;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = classNames(styles.dropdownContent, className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== wrapperClassName) {
    t4 = classNames(styles.scaleTransition, wrapperClassName);
    $[4] = wrapperClassName;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== trigger) {
    t5 = <DropdownTriggerEventHandler>{trigger}</DropdownTriggerEventHandler>;
    $[6] = trigger;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== Anchor || $[9] !== alignContent || $[10] !== anchorClassName || $[11] !== id || $[12] !== marginInPx || $[13] !== preferredPopupPosition || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <Popup id={id} content={t2} contentClassName={t3} contentWrapperClassName={t4} popupOpenWrapperClassName={styles.scaleTransitionOpen} triggerEvents={triggerEvents} preferredPopupPosition={preferredPopupPosition} alignPopup={alignContent} anchorClassName={anchorClassName} Anchor={Anchor} marginInPx={marginInPx}>{t5}</Popup>;
    $[8] = Anchor;
    $[9] = alignContent;
    $[10] = anchorClassName;
    $[11] = id;
    $[12] = marginInPx;
    $[13] = preferredPopupPosition;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
type DropDownContext = Pick<PopupContext, 'showPopup' | 'hidePopup' | 'open' | 'togglePopup' | 'spyRef' | 'popupRef' | 'alignPopup' | 'preferredPopupPosition'> & {
  parentContext?: DropDownContext;
};
export function useDropdownContext() {
  const $ = _c(10);
  const {
    showPopup,
    hidePopup,
    togglePopup,
    spyRef,
    popupRef,
    inDOM: open,
    parentContext: ctx,
    alignPopup,
    preferredPopupPosition
  } = usePopupContext();
  const parentContext = ctx && ctx.initialised ? ctx as DropDownContext : undefined;
  let t0;
  if ($[0] !== alignPopup || $[1] !== hidePopup || $[2] !== open || $[3] !== parentContext || $[4] !== popupRef || $[5] !== preferredPopupPosition || $[6] !== showPopup || $[7] !== spyRef || $[8] !== togglePopup) {
    t0 = {
      open,
      showPopup,
      hidePopup,
      togglePopup,
      spyRef,
      popupRef,
      parentContext,
      alignPopup,
      preferredPopupPosition
    };
    $[0] = alignPopup;
    $[1] = hidePopup;
    $[2] = open;
    $[3] = parentContext;
    $[4] = popupRef;
    $[5] = preferredPopupPosition;
    $[6] = showPopup;
    $[7] = spyRef;
    $[8] = togglePopup;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  return t0 as const;
}
export default Dropdown;